import { useEffect, useMemo, useState } from 'react'
import {
  type CompactPlan,
  Mtds,
  EmptyState,
  TopBar,
  AppLayout,
  useAuth,
  useGetNewsArticleProvePlanQuery,
  useGetPlansQuery,
  useGetWorkspacesQuery,
  useBetaFeature,
  WhatsNew,
  filterPlans,
  PlansToggleValue,
  ScreenHeader
} from 'prove-common'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { CreatePlanButton } from '../components/create-plan-button/CreatePlanButton'
import { Navigate, useSearchParams } from 'react-router'
import { PlansScreenToggleGroup } from '../components/plans-screen-toggle-group/PlansScreenToggleGroup'
import { AppLogo } from '../components/app-logo/AppLogo'
import { PlanCard } from '../components/plan-card/PlanCard'
import { WorkspaceTasksCard } from '../components/workspace-tasks-card/WorkspaceTasksCard'
import { useTaskOverviewRows } from '../hooks/usePlanTasks'
import { getWorkspaceTaskSummary } from '../utils/tasksTableUtils'
import { TASKS_BETA_FEATURE_KEY } from '../betaFeatures'
import {
  collectRecursiveWorkspacePlanIds,
  defaultWorkspaceId,
  filterPlansByWorkspace,
  filterUnassignedPlans,
  getWorkspaceById,
  isUnassignedSelection,
  isWorkspaceId,
  parseWorkspaceSelection,
  UNASSIGNED_WORKSPACE,
  unassignedWorkspaceDescription,
  unassignedWorkspaceName,
  FAGSEKSJON,
  useIsWorkspaceFeatureEnabled,
  WORKSPACE_QUERY_PARAM,
  toWorkspaceViewModels,
  type WorkspaceId,
  type WorkspaceSelection
} from '../workspaces/workspaces'

const yearFromDate = (date?: string) =>
  date == null ? undefined : new Date(date).getFullYear().toString()

const isString = (value: unknown): value is string => typeof value === 'string'
const validToggleValues: PlansToggleValue[] = [
  'active',
  'draft',
  'deactivated',
  'all'
]
const toValidToggleValue = (value: string | null): PlansToggleValue =>
  validToggleValues.includes(value as PlansToggleValue)
    ? (value as PlansToggleValue)
    : 'all'

const resolveSelectedWorkspace = (
  workspaceSelection: WorkspaceSelection | undefined,
  workspaceId: WorkspaceId | undefined,
  workspaces: ReturnType<typeof toWorkspaceViewModels>
): WorkspaceSelection | undefined => {
  if (isUnassignedSelection(workspaceSelection)) return UNASSIGNED_WORKSPACE
  if (isWorkspaceId(workspaceId, workspaces)) return workspaceId
  return defaultWorkspaceId(workspaces)
}

const getDisplayedPlans = (
  topLevelPlans: CompactPlan[],
  isWorkspaceFeatureEnabled: boolean,
  selectedWorkspaceId: WorkspaceId | undefined,
  workspaces: ReturnType<typeof toWorkspaceViewModels>,
  kind?: string
): CompactPlan[] => {
  if (!isWorkspaceFeatureEnabled) return topLevelPlans
  if (selectedWorkspaceId == null) {
    return filterUnassignedPlans(topLevelPlans, workspaces, kind)
  }
  return filterPlansByWorkspace(topLevelPlans, selectedWorkspaceId)
}

const getWorkspaceHeader = (
  isWorkspaceFeatureEnabled: boolean,
  isUnassignedView: boolean,
  selectedWorkspaceModel: ReturnType<typeof getWorkspaceById> | undefined,
  kind?: string
) => {
  if (!isWorkspaceFeatureEnabled) return <ScreenHeader title='Planer' />

  if (isUnassignedView) {
    return (
      <ScreenHeader
        title={unassignedWorkspaceName(kind)}
        description={<p>{unassignedWorkspaceDescription(kind)}</p>}
      />
    )
  }

  return (
    <Mtds.Flex data-align={'start'} data-justify={'space-between'} data-nowrap>
      <Mtds.Grid data-gap={3}>
        <ScreenHeader
          title={selectedWorkspaceModel?.name}
          description={<p>{selectedWorkspaceModel?.description}</p>}
        />
      </Mtds.Grid>
      <img
        alt=''
        aria-hidden='true'
        className='hidden h-40 w-40 object-contain lg:block'
        src={selectedWorkspaceModel?.illustration}
      />
    </Mtds.Flex>
  )
}

export default function PlansScreen() {
  const workspaceFeature = useIsWorkspaceFeatureEnabled()
  const isWorkspaceFeatureEnabled = workspaceFeature.enabled
  const isTasksFeatureEnabled = useBetaFeature(TASKS_BETA_FEATURE_KEY).enabled
  const [searchParams, setSearchParams] = useSearchParams()
  const kind = searchParams.get('kind') ?? undefined
  const workspaceSelectionParam = parseWorkspaceSelection(
    searchParams.get(WORKSPACE_QUERY_PARAM)
  )
  const toggleValueParam = searchParams.get('toggle')
  const workspaceIdParam = isUnassignedSelection(workspaceSelectionParam)
    ? undefined
    : workspaceSelectionParam
  const {
    data: workspaceData,
    isPending: isPendingWorkspaces,
    isError: isErrorWorkspaces
  } = useGetWorkspacesQuery(isWorkspaceFeatureEnabled)
  const workspaces = toWorkspaceViewModels(workspaceData)
  const hasValidWorkspaceSelection =
    isUnassignedSelection(workspaceSelectionParam) ||
    isWorkspaceId(workspaceIdParam, workspaces)
  const selectedWorkspace = resolveSelectedWorkspace(
    workspaceSelectionParam,
    workspaceIdParam,
    workspaces
  )
  const isUnassignedView = isUnassignedSelection(selectedWorkspace)
  const selectedWorkspaceId: WorkspaceId | undefined = isUnassignedSelection(
    selectedWorkspace
  )
    ? undefined
    : selectedWorkspace
  const selectedWorkspaceModel = isUnassignedView
    ? undefined
    : getWorkspaceById(workspaces, selectedWorkspaceId)
  // When the workspace feature is on and no specific (valid) workspace is
  // selected, default to the "Vis alle" overview instead of a single workspace.
  const isWorkspacesLoading = isWorkspaceFeatureEnabled && isPendingWorkspaces
  const canValidateWorkspaceSelection =
    isWorkspaceFeatureEnabled &&
    workspaceFeature.isPending === false &&
    isWorkspacesLoading === false &&
    workspaceData !== undefined
  const shouldRedirectToOverview =
    canValidateWorkspaceSelection && hasValidWorkspaceSelection === false
  // Initialize toggleValue from URL param or fallback to localStorage or 'all'
  const [toggleValue, setToggleValue] = useState<PlansToggleValue>(() =>
    toValidToggleValue(
      toggleValueParam ??
        localStorage.getItem('localSettings.plans.toggleValue')
    )
  )
  const [selectedYear, setSelectedYear] = useState('all')

  // Persist toggleValue to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('localSettings.plans.toggleValue', toggleValue)
  }, [toggleValue])

  const {
    data: plans,
    isPending: isPendingPlans,
    isFetching: isFetchingPlans,
    isError: isErrorPlans
  } = useGetPlansQuery()
  const { isAdmin, permissions = [] } = useAuth()
  const { data: articleData } = useGetNewsArticleProvePlanQuery()
  useEffect(() => {
    // Wait until the feature flag and workspaces have resolved before syncing
    // the workspace param, otherwise we would write the fallback workspace id
    // before knowing whether the overview redirect should happen.
    if (
      workspaceFeature.isPending ||
      isWorkspacesLoading ||
      shouldRedirectToOverview
    )
      return

    const next = new URLSearchParams(searchParams)
    next.set('toggle', toggleValue.toString())
    if (isWorkspaceFeatureEnabled) {
      next.set(
        WORKSPACE_QUERY_PARAM,
        selectedWorkspaceId == null
          ? UNASSIGNED_WORKSPACE
          : selectedWorkspaceId.toString()
      )
    } else {
      next.delete(WORKSPACE_QUERY_PARAM)
    }
    setSearchParams(next, { replace: true })
  }, [
    isWorkspaceFeatureEnabled,
    isWorkspacesLoading,
    selectedWorkspaceId,
    shouldRedirectToOverview,
    searchParams,
    setSearchParams,
    toggleValue,
    workspaceFeature.isPending
  ])
  const topLevelPlans = (plans?.plans ?? []).filter(
    (plan) => (plan?.parents?.length ?? 0) === 0
  )
  const displayedPlans = getDisplayedPlans(
    topLevelPlans,
    isWorkspaceFeatureEnabled,
    selectedWorkspaceId,
    workspaces,
    kind
  )
  const yearOptions = [
    { value: 'all', label: 'Alle år' },
    ...Array.from(
      new Set(
        displayedPlans.map((plan) => yearFromDate(plan.due)).filter(isString)
      )
    )
      .sort((a, b) => Number(b) - Number(a))
      .map((year) => ({ value: year, label: year }))
  ]
  const isLoading = isPendingPlans || isFetchingPlans || isWorkspacesLoading
  const isError =
    isErrorPlans || (isWorkspaceFeatureEnabled && isErrorWorkspaces)
  const filteredPlans = filterPlans(
    displayedPlans,
    undefined,
    [],
    toggleValue
  ).filter(
    (plan) => selectedYear === 'all' || yearFromDate(plan.due) === selectedYear
  )
  const workspaceHeader = getWorkspaceHeader(
    isWorkspaceFeatureEnabled,
    isUnassignedView,
    selectedWorkspaceModel,
    kind
  )
  const createPlanWorkspaceId = isWorkspaceFeatureEnabled
    ? selectedWorkspaceId
    : undefined
  const { rows: taskRows, today } = useTaskOverviewRows()
  const workspaceTaskPlanIds = useMemo(
    () =>
      selectedWorkspaceId == null
        ? undefined
        : collectRecursiveWorkspacePlanIds(plans?.plans, selectedWorkspaceId),
    [plans?.plans, selectedWorkspaceId]
  )
  const workspaceTaskSummary = useMemo(
    () => getWorkspaceTaskSummary(taskRows, workspaceTaskPlanIds, today),
    [taskRows, today, workspaceTaskPlanIds]
  )

  if (shouldRedirectToOverview) {
    return <Navigate to='/workspaces' replace />
  }

  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      isLoading={isLoading}
      isError={isError}
      topBar={
        <TopBar
          logo={<AppLogo />}
          title={
            isWorkspaceFeatureEnabled
              ? (selectedWorkspaceModel?.kind?.title ??
                workspaces.find((workspace) => workspace.kind?.code === kind)
                  ?.kind?.title ??
                'Arbeidsområder')
              : 'Planer'
          }
          showNotificationBell={true}
        />
      }
      maxWidth={1000}
    >
      {!isLoading && !isError && (
        <Mtds.Grid data-gap='8' className='w-full max-w-5xl mx-auto'>
          {workspaceHeader}
          {isTasksFeatureEnabled && selectedWorkspaceId != null && (
            <WorkspaceTasksCard
              summary={workspaceTaskSummary}
              to={`/tasks?${WORKSPACE_QUERY_PARAM}=${selectedWorkspaceId}`}
            />
          )}
          <Mtds.Flex data-align='center' data-justify='space-between'>
            <Mtds.Flex data-align={'center'}>
              <PlansScreenToggleGroup
                toggleValue={toggleValue}
                setToggleValue={setToggleValue}
                plans={displayedPlans}
                className='w-40'
              />
              <Mtds.Field
                as='select'
                aria-label='Filtrer på år'
                value={selectedYear}
                onChange={({ target }) => setSelectedYear(target.value)}
                options={yearOptions}
                className='w-40'
              />
            </Mtds.Flex>
            <Mtds.Flex>
              {(isAdmin ||
                permissions.some(
                  (permission) =>
                    permission.responsibility === 'PROGRAM_MANAGER'
                )) &&
                (!isWorkspaceFeatureEnabled ||
                  selectedWorkspaceModel?.kind?.code === FAGSEKSJON) && (
                  <CreatePlanButton workspaceId={createPlanWorkspaceId} />
                )}
            </Mtds.Flex>
          </Mtds.Flex>
          <Mtds.Grid className='w-full'>
            {filteredPlans.length === 0 ? (
              <EmptyState
                title='Ingen planer'
                description='Ingen planer å vise for de valgte filtrene.'
              />
            ) : (
              filteredPlans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} />
              ))
            )}
          </Mtds.Grid>
        </Mtds.Grid>
      )}
      {articleData && <WhatsNew article={articleData} />}
    </AppLayout>
  )
}
